import { useQueryClient } from '@tanstack/react-query';
import { payments } from '@lawbee/domain';
import { Alert, Badge, Button, Checkbox, Dialog, DialogContent, Field, FormError, Input, Select, Textarea, formatMoney } from '@lawbee/ui';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage, fieldErrors } from '../../../lib/api';
import { dollarsToCents, useApiMutation, useCopy } from '../../../lib/hooks';
import { useMe } from '../../../lib/me';
import type { Invoice, MatterDetail, PaymentKind } from '../../../lib/types';
import { fullName, useMatterInvalidate } from './shared';

const KIND_OPTIONS = payments.PAYMENT_KINDS.map((k) => ({ value: k, label: payments.PAYMENT_KIND_LABELS[k] }));

const DEFAULT_DESCRIPTION: Record<PaymentKind, string> = {
  retainer: 'Retainer',
  invoice: 'Legal fees',
  consultation_fee: 'Consultation fee',
};

export function NewInvoiceDialog({ open, onOpenChange, data }: { open: boolean; onOpenChange: (o: boolean) => void; data: MatterDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const invalidate = useMatterInvalidate(data.matter.id);
  const { copy, copied } = useCopy();
  const client = data.matter.client;
  const [kind, setKind] = useState<PaymentKind>('retainer');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [send, setSend] = useState(!!client?.email);
  const [result, setResult] = useState<{ invoice: Invoice; payUrl: string } | null>(null);
  const destination = me.settings.paymentRouting[kind];
  const cents = dollarsToCents(amount);

  const create = useApiMutation(
    () =>
      api.post<{ invoice: Invoice; payUrl: string }>('/api/payments/invoices', {
        kind,
        matterId: data.matter.id,
        contactId: client?.id ?? null,
        description: description.trim() || `${DEFAULT_DESCRIPTION[kind]} - ${data.matter.title}`.slice(0, 300),
        amountCents: cents,
        dueOn: dueOn || null,
        payerEmail: client?.email ?? null,
        payerName: client ? fullName(client.firstName, client.lastName) : null,
        send: !!client?.email && send,
      }),
    {
      success: (res) => (res.invoice.status === 'sent' || (send && client?.email) ? 'Invoice created and sent' : 'Invoice created'),
      errorTitle: 'Could not create the invoice',
      onSuccess: (res) => {
        setResult(res);
        void invalidate();
        void qc.invalidateQueries({ queryKey: ['invoices'] });
      },
    },
  );
  const errs = fieldErrors(create.error);

  const close = (o: boolean) => {
    onOpenChange(o);
    if (!o) {
      setResult(null);
      setDescription('');
      setAmount('');
      setDueOn('');
      create.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        size="lg"
        title={result ? `Invoice ${result.invoice.number}` : 'New invoice'}
        description={result ? undefined : `For ${data.matter.title}`}
        footer={
          result ? (
            <Button onClick={() => close(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button type="submit" form="new-invoice" loading={create.isPending} disabled={cents < 100}>
                {client?.email && send ? 'Create and send' : 'Create invoice'}
              </Button>
            </>
          )
        }
      >
        {result ? (
          <div className="space-y-4">
            <Alert variant="success" title={`${formatMoney(result.invoice.amountCents)} ${result.invoice.destination === 'trust' ? 'to the trust account' : 'to the operating account'}`}>
              {send && client?.email ? `A payment link was emailed to ${client.email}.` : 'Share the payment link with the client.'}
            </Alert>
            <Field label="Payment link" htmlFor="pay-url">
              <div className="flex gap-2">
                <Input id="pay-url" readOnly value={result.payUrl} className="font-mono text-[12px]" onFocus={(e) => e.currentTarget.select()} />
                <Button variant="outline" onClick={() => void copy(result.payUrl, 'Payment link copied')} aria-label="Copy payment link">
                  {copied === result.payUrl ? <Check aria-hidden /> : <Copy aria-hidden />}
                </Button>
              </div>
            </Field>
          </div>
        ) : (
          <form
            id="new-invoice"
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (cents >= 100) create.mutate(undefined);
            }}
          >
            <Field label="Kind" htmlFor="inv-kind" error={errs.kind}>
              <Select id="inv-kind" value={kind} onChange={(e) => setKind(e.target.value as PaymentKind)} options={KIND_OPTIONS} />
            </Field>
            <p className="flex items-center gap-2 text-sm">
              {destination === 'trust' ? <Badge variant="primary">Trust</Badge> : <Badge variant="outline">Operating</Badge>}
              <span className="text-muted-foreground">{destination === 'trust' ? 'Goes to trust account' : 'Goes to operating account'} (set in payment settings)</span>
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Amount (CAD)" htmlFor="inv-amount" required error={errs.amountCents ?? (amount && cents < 100 ? 'At least $1.00.' : undefined)}>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input id="inv-amount" inputMode="decimal" className="figures pl-7" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2,500.00" />
                </div>
              </Field>
              <Field label="Due date" htmlFor="inv-due" error={errs.dueOn}>
                <Input id="inv-due" type="date" className="figures" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
              </Field>
            </div>
            <Field label="Description" htmlFor="inv-desc" description="Shown to the client on the payment page." error={errs.description}>
              <Textarea id="inv-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={`${DEFAULT_DESCRIPTION[kind]} - ${data.matter.title}`} />
            </Field>
            <div className="space-y-1">
              <Checkbox label={client?.email ? `Email the payment link to ${client.email}` : 'Email the payment link to the client'} checked={!!client?.email && send} disabled={!client?.email} onCheckedChange={(c) => setSend(c === true)} />
              {!client?.email ? <p className="pl-[26px] text-xs text-muted-foreground">The client has no email address. Copy the link after creating the invoice.</p> : null}
            </div>
            <p className="text-[12px] leading-relaxed text-muted-foreground">{payments.TRUST_COMPLIANCE_NOTICE}</p>
            {create.error && Object.keys(errs).length === 0 ? <FormError message={errorMessage(create.error)} /> : null}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
